import React, { useEffect, useMemo, useState } from 'react'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Checkbox from '@mui/material/Checkbox'
import Button from '@mui/material/Button'
import TableSkeletonRows from '../../../components/Skeleton/TableSkeletonRows'
import { formatMoney } from '../../../utils/amount'
import { formatDate, EM_DASH } from './helpers'

const PAGE_SIZE = 50
const COLUMN_COUNT = 9
const NO_CLIENT_RECORD = 'No client record'

const SkippedPaymentsTable = ({ rows = [], loading = false, onRestore = () => {}, restoring = false }) => {
  const [selected, setSelected] = useState({})
  const [page, setPage] = useState(0)

  useEffect(() => {
    setSelected((prev) => {
      const present = new Set(rows.map((row) => row.paymentId))
      const kept = Object.keys(prev).filter((paymentId) => present.has(paymentId))
      if (kept.length === Object.keys(prev).length) return prev
      return kept.reduce((acc, paymentId) => ({ ...acc, [paymentId]: true }), {})
    })
  }, [rows])

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pagedRows = useMemo(
    () => rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [rows, safePage]
  )
  const selectedIds = useMemo(
    () => rows.filter((row) => selected[row.paymentId]).map((row) => row.paymentId),
    [rows, selected]
  )

  const pageSelectedCount = pagedRows.filter((row) => selected[row.paymentId]).length
  const allPageSelected = pagedRows.length > 0 && pageSelectedCount === pagedRows.length
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected
  const showSkeleton = loading && rows.length === 0

  const toggleRow = (paymentId) =>
    setSelected((prev) => {
      const next = { ...prev }
      if (next[paymentId]) delete next[paymentId]
      else next[paymentId] = true
      return next
    })

  const togglePage = (checked) =>
    setSelected((prev) => {
      const next = { ...prev }
      pagedRows.forEach((row) => {
        if (checked) next[row.paymentId] = true
        else delete next[row.paymentId]
      })
      return next
    })

  const handleRestoreSelected = () => {
    onRestore(selectedIds)
    setSelected({})
  }

  return (
    <div className="rounded-2xl border border-divider bg-paper overflow-hidden" data-testid="skipped-table">
      <div className="flex items-center justify-end px-4 py-3 border-b border-divider">
        <Button
          size="small"
          variant="outlined"
          data-testid="restore-selected"
          disabled={selectedIds.length === 0 || restoring}
          onClick={handleRestoreSelected}
        >
          {`Restore selected (${selectedIds.length})`}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={(event) => togglePage(event.target.checked)}
                  inputProps={{ 'aria-label': 'select all skipped payments on this page' }}
                />
              </TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Skipped By</TableCell>
              <TableCell>Skipped On</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton ? (
              <TableSkeletonRows colSpan={COLUMN_COUNT} />
            ) : (
              <>
                {pagedRows.map((row) => (
                  <TableRow key={row.paymentId} hover data-testid={`skipped-row-${row.paymentId}`}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={!!selected[row.paymentId]}
                        onChange={() => toggleRow(row.paymentId)}
                        inputProps={{ 'aria-label': `select ${row.clientName || NO_CLIENT_RECORD}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-text-primary">{row.clientName || NO_CLIENT_RECORD}</p>
                      {row.clientCode && <p className="text-xs text-text-secondary">{row.clientCode}</p>}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-text-primary">{formatMoney(row.amount)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-text-primary">{`#${row.pmt}`}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-text-primary">{formatDate(row.dueDate)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-text-primary">{row.exclusion?.byName || EM_DASH}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-text-primary">{formatDate(row.exclusion?.at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-text-primary break-words">{row.exclusion?.reason || EM_DASH}</span>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        data-testid={`restore-btn-${row.paymentId}`}
                        disabled={restoring}
                        onClick={() => onRestore([row.paymentId])}
                      >
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <p className="text-sm text-text-secondary text-center py-8">
                        No skipped payments in this date range.
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {rows.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-divider">
          <span className="text-xs text-text-secondary" data-testid="pagination-summary">
            Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="small"
              variant="outlined"
              data-testid="page-prev"
              disabled={safePage === 0}
              onClick={() => setPage(Math.max(0, safePage - 1))}
            >
              Previous
            </Button>
            <span className="text-xs text-text-secondary" data-testid="page-indicator">
              Page {safePage + 1} of {pageCount}
            </span>
            <Button
              size="small"
              variant="outlined"
              data-testid="page-next"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SkippedPaymentsTable
